/**
 * Composition root — load plugins, construct ExhibitApp + ControlPanel, bind.
 *
 * Data flow (single direction):
 *   ControlPanel  --onControlPatch / onPreset / transport-->  ExhibitApp
 *   ExhibitApp    --subscribe -->  panel.syncFromApp + chrome
 *
 * ExhibitApp is the sole owner of sim/render/preset/dirty. The panel never
 * holds a writeable copy of params.
 */

import { DISTURB_HELP } from "./app/choreography";
import { ExhibitApp } from "./app/ExhibitApp";
import { loadBuiltinPlugins } from "./plugins";
import { ControlPanel } from "./ui/controls";
import { DistanceMatrixView } from "./ui/distanceMatrix";
import { MetricsPanel } from "./ui/metricsPanel";
import { StoryTour } from "./ui/storyTour";
import { bindChoreoChrome } from "./ui/transport/choreo";
import { bindTransportTip } from "./ui/transport/tip";
import { bindToolChrome } from "./ui/transport/tools";

loadBuiltinPlugins();

function boot(): void {
  const canvas = document.getElementById("stage") as HTMLCanvasElement;
  const stageHost =
    document.querySelector(".stage-host") ??
    document.querySelector(".stage-wrap");
  if (!canvas || !stageHost) throw new Error("missing stage elements");

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  const app = new ExhibitApp(canvas, { reducedMotion });

  const metricsHost = document.getElementById("insights-root");
  const matrixHost = document.getElementById("distance-matrix-root");
  const storyHost = document.getElementById("story-root");
  const metrics = metricsHost ? new MetricsPanel(metricsHost) : null;
  const matrix = matrixHost ? new DistanceMatrixView(matrixHost) : null;

  const panel = new ControlPanel(
    app.params,
    app.render,
    app.presetId,
    {
      onControlPatch(path, value, opts) {
        app.patchControl(path, value, opts);
      },
      onPreset(p) {
        app.applyPreset(p);
      },
      onPlayToggle() {
        app.togglePlay();
      },
      onStep() {
        app.stepOnce();
      },
      onReset() {
        app.reset();
      },
      onReseed() {
        app.reseed();
      },
      onStepsPerFrame(n) {
        app.setStepsPerFrame(n);
      },
      onShare() {
        app.share();
      },
      onGuideMode(id) {
        app.setGuideMode(id);
      },
    },
    app.guideModeId,
  );

  const recordBtn = document.getElementById(
    "btn-record",
  ) as HTMLButtonElement | null;
  if (recordBtn) {
    if (!app.canRecord) {
      recordBtn.disabled = true;
      recordBtn.title = "WebM recording not supported in this browser";
    }
    recordBtn.addEventListener("click", () => {
      app.toggleRecord();
      recordBtn.textContent = app.recording ? "Stop" : "Record";
      recordBtn.classList.toggle("primary", app.recording);
    });
  }

  const disturbBtn = document.getElementById("btn-disturb");
  if (disturbBtn) {
    disturbBtn.title = `${DISTURB_HELP.summary}\n\n${DISTURB_HELP.detail}`;
    disturbBtn.addEventListener("click", () => app.disturbRandomFriend());
  }

  const syncToolUi = bindToolChrome(app);
  const syncChoreoUi = bindChoreoChrome(app);

  const tipBtn = document.getElementById(
    "btn-transport-tip",
  ) as HTMLButtonElement | null;
  const tipPop = document.getElementById("transport-tip");
  if (tipBtn && tipPop) bindTransportTip(tipBtn, tipPop);

  const story =
    storyHost != null
      ? new StoryTour(storyHost, {
          onPreset(p) {
            app.applyPreset(p);
          },
        })
      : null;

  const syncPresetChrome = (): void => {
    const p = app.getPreset();
    const storyEl = document.getElementById("preset-story");
    const citeEl = document.getElementById("preset-citation");
    if (storyEl) storyEl.textContent = p.story;
    if (citeEl) citeEl.textContent = p.citation;
    story?.syncPreset(app.presetId);
  };

  const pullPanel = (): void => {
    panel.syncFromApp({
      sim: app.params,
      render: app.render,
      audio: app.audio,
      presetId: app.presetId,
      dirty: app.dirty,
      guideModeId: app.guideModeId,
    });
  };

  // Initial paint
  pullPanel();
  syncPresetChrome();
  panel.setPlaying(app.playing);
  panel.setStepsPerFrame(app.stepsPerFrame);
  panel.setEquation(app.params);

  let lastPresetId = app.presetId;
  let lastChoreoStatus = app.choreographyStatus;
  let lastToolSig = "";

  const syncInsights = (): void => {
    const r = app.render;
    if (metrics && metricsHost) {
      metricsHost.classList.toggle("hidden", !r.showMetrics);
      if (r.showMetrics) metrics.update(app.sim, r.selectedIndex);
    }
    if (matrix && matrixHost) {
      matrixHost.classList.toggle("hidden", !r.showDistanceMatrix);
      if (r.showDistanceMatrix) {
        matrix.setSize(r.distanceMatrixSize);
        matrix.draw(app.sim.positions, app.sim.dim, app.sim.n);
      }
    }
  };
  syncInsights();

  app.subscribe(() => {
    panel.setPlaying(app.playing);
    const hud = document.getElementById("perf-hud");
    if (hud) {
      hud.textContent = app.perfText();
      hud.title = app.perfTitle();
    }
    // Load guard chip: what is temporarily paused + which levers free budget.
    // Lab prefs are never rewritten — only the working frame is eased.
    const qChip = document.getElementById("quality-status");
    if (qChip) {
      const q = app.qualityReport();
      if (q.active && q.chip) {
        qChip.hidden = false;
        qChip.textContent = q.chip;
        qChip.title = q.detail;
        qChip.dataset.severity = q.severity;
      } else {
        qChip.hidden = true;
        qChip.textContent = "";
        qChip.removeAttribute("data-severity");
      }
    }
    // Equation follows app params (cheap); full control paint is gated by interaction lock
    panel.setEquation(app.params);
    pullPanel();
    syncInsights();

    const st = app.choreographyStatus;
    if (st !== lastChoreoStatus) {
      lastChoreoStatus = st;
      syncChoreoUi();
    }

    const toolSig = `${app.pointerTool}|${app.spawnModeId}|${app.render.selectedIndex}|${app.playing}|${app.sim.n}|${app.getToolFlashText() ?? ""}`;
    if (toolSig !== lastToolSig) {
      lastToolSig = toolSig;
      syncToolUi();
    }

    if (app.presetId !== lastPresetId) {
      lastPresetId = app.presetId;
      syncPresetChrome();
    }
  });

  app.start(stageHost);
}

boot();
